
































































import { Component, Emit, Vue } from 'vue-property-decorator';
import GameOver from './components/GameOver.vue';
import ScoreButton from './components/ScoreButton.vue';
import Settings from './components/Settings.vue';
import { defaultOrder, defaultPlayerHits } from './defaults';
import {
  Closed,
  ClosedType,
  HitIdType,
  PlayerHits,
  PlayerIdType,
  Scores,
  Timeline,
  TimelineEntry,
  Theme,
  WinningType,
} from './types';
import {
  CLOSED_0,
  CLOSED_1,
  HIT_ID_15,
  HIT_ID_16,
  HIT_ID_17,
  HIT_ID_18,
  HIT_ID_19,
  HIT_ID_20,
  HIT_ID_B,
  PLAYER_ID_A,
  PLAYER_ID_B,
  PLAYER_ID_C,
  PLAYER_ID_D,
  TIE,
  THEMES,
} from './constants';

@Component({
  components: { GameOver, ScoreButton, Settings },
})
export default class App extends Vue {
  version: string = '0.1';
  openSettings: boolean = false;
  order: ReadonlyArray<HitIdType> = defaultOrder;
  playerHits: PlayerHits = defaultPlayerHits;
  seenWinner: boolean = false;
  timeline: Timeline = [];
  themes: Array<Theme> = THEMES;
  themeIdx: number = 0;

  get closed(): Closed {
    const { A, B, C, D } = this.playerHits;
    const check = (k: HitIdType): ClosedType =>
      A[k] >= 3 && B[k] >= 3 ? CLOSED_1 : CLOSED_0;
    return {
      [HIT_ID_20]: check(HIT_ID_20),
      [HIT_ID_19]: check(HIT_ID_19),
      [HIT_ID_18]: check(HIT_ID_18),
      [HIT_ID_17]: check(HIT_ID_17),
      [HIT_ID_16]: check(HIT_ID_16),
      [HIT_ID_15]: check(HIT_ID_15),
      [HIT_ID_B]: check(HIT_ID_B),
    };
  }

  get gameOver(): boolean {
    const closedA = (): boolean => this.closedHits(PLAYER_ID_A).length === 0;
    const closedB = (): boolean => this.closedHits(PLAYER_ID_B).length === 0;
    const closedC = (): boolean => this.closedHits(PLAYER_ID_C).length === 0;
    const closedD = (): boolean => this.closedHits(PLAYER_ID_D).length === 0;
    const allClosed: boolean =
      Object.values(this.closed).filter(a => a === 0).length === 0;
    const winningA = this.winning === PLAYER_ID_A;
    const winningB = this.winning === PLAYER_ID_B;
    const winningC = this.winning === PLAYER_ID_C;
    const winningD = this.winning === PLAYER_ID_D;
    return (
      allClosed ||
      (winningA && closedA()) ||
      (winningB && closedB()) ||
      (winningC && closedC()) ||
      (winningD && closedD())
    );
  }

  get theme(): Theme {
    return this.themes[this.themeIdx || 0];
  }

  get winning(): WinningType {
    const [a, b, c, d] = Object.values(this.scores);
    const max = Math.max(a, b, c, d);
    const winning: Array<PlayerIdType> = [];
    if (this.scores[PLAYER_ID_A] === max) winning.push(PLAYER_ID_A);
    if (this.scores[PLAYER_ID_B] === max) winning.push(PLAYER_ID_B);
    if (this.scores[PLAYER_ID_C] === max) winning.push(PLAYER_ID_C);
    if (this.scores[PLAYER_ID_D] === max) winning.push(PLAYER_ID_D);
    return winning.length > 1 ? TIE : winning[0];
  }

  get winner(): WinningType | null {
    if (!this.gameOver) return null;
    return this.winning;
  }

  get winnerText(): string | null {
    if (!this.winner) return null;
    return this.winner === TIE ? TIE : `Player ${this.winner}`;
  }

  get scores(): Scores {
    let a: number = 0;
    let b: number = 0;
    let c: number = 0;
    let d: number = 0;
    const { order, playerHits } = this;
    order.forEach(key => {
      const val: number = key === HIT_ID_B ? 25 : parseInt(key);
      if (playerHits[PLAYER_ID_A][key] > 3)
        a += (playerHits[PLAYER_ID_A][key] - 3) * val;
      if (playerHits[PLAYER_ID_B][key] > 3)
        b += (playerHits[PLAYER_ID_B][key] - 3) * val;
      if (playerHits[PLAYER_ID_C][key] > 3)
        c += (playerHits[PLAYER_ID_C][key] - 3) * val;
      if (playerHits[PLAYER_ID_D][key] > 3)
        d += (playerHits[PLAYER_ID_D][key] - 3) * val;
    });
    return {
      [PLAYER_ID_A]: a,
      [PLAYER_ID_B]: b,
      [PLAYER_ID_C]: c,
      [PLAYER_ID_D]: d,
    };
  }

  closedHits(playerId: PlayerIdType) {
    return Object.values(this.playerHits[playerId]).filter(a => a < 3);
  }

  @Emit() clear(): void {
    if (window.confirm('Are you sure you want a new game?')) {
      for (let key in this.playerHits) {
        for (let k in this.playerHits[key as PlayerIdType]) {
          this.$set(this.playerHits[key as PlayerIdType], k, 0);
        }
      }
      this.seenWinner = false;
      while (this.timeline.length) this.timeline.pop();
      this.$nextTick(() => this.save());
    }
  }

  @Emit() closeGameOver(): void {
    this.seenWinner = true;
  }

  @Emit() hit(playerId: PlayerIdType, hitId: HitIdType): void {
    const currVal = this.playerHits[playerId][hitId];
    const nextVal = currVal + 1;
    if (this.closed[hitId] || this.gameOver) return;
    const entry: TimelineEntry = { playerId, hitId };
    this.timeline.push(entry);
    this.$set(this.playerHits[playerId], hitId, nextVal);
    this.save();
  }

  @Emit() save(): void {
    const state = {
      playerHits: this.playerHits,
      timeline: this.timeline,
    };
    localStorage.setItem('cricket', JSON.stringify(state));
  }

  @Emit() selectTheme(idx: number): void {
    this.themeIdx = idx;
    localStorage.setItem('theme', idx.toString());
  }

  @Emit() toggleSettings(): void {
    this.openSettings = !this.openSettings;
  }

  @Emit() undo(): void {
    if (!this.timeline.length) return;
    const entry = this.timeline.pop();
    if (!entry) return;
    const hitId: HitIdType = entry.hitId;
    const playerId: PlayerIdType = entry.playerId;
    const nextVal = this.playerHits[playerId][hitId] - 1;
    this.seenWinner = false;
    this.$set(this.playerHits[playerId], hitId, nextVal);
    this.save();
  }

  mounted(): void {
    const version = localStorage.getItem('version');
    if (!version || version !== this.version) {
      localStorage.setItem('version', this.version);
      localStorage.removeItem('cricket');
      localStorage.removeItem('theme');
    }
    const theme = localStorage.getItem('theme');
    if (theme) this.themeIdx = parseInt(theme);

    const saved = localStorage.getItem('cricket');
    if (saved) {
      const { playerHits, timeline } = JSON.parse(saved);
      this.$set(this, 'playerHits', playerHits as PlayerHits);
      this.$set(this, 'timeline', timeline as Timeline);
    }
  }
}
